/**
 * lucideIconData.js — Categorized, searchable index of Lucide icons.
 * 
 * We dynamically pull icon names from the lucide-react package at build time.
 * Categories are manually curated for the most commonly used icons.
 * Icons not in any category appear under "Other".
 */
import { icons } from 'lucide-react';

// All available icon names from lucide-react
export const ALL_ICON_NAMES = Object.keys(icons);

// Curated categories with icon names
export const ICON_CATEGORIES = [
    {
        key: 'common',
        label: 'Common',
        icons: [
            'Home', 'Search', 'Settings', 'Bell', 'Star', 'Heart', 'User', 'Users',
            'Check', 'X', 'Plus', 'Minus', 'ChevronRight', 'ArrowRight', 'ExternalLink',
            'Eye', 'EyeOff', 'Lock', 'Unlock', 'Shield', 'Key', 'Globe', 'Zap',
            'Sparkles', 'Flame', 'Sun', 'Moon', 'Cloud', 'Smile', 'ThumbsUp',
            'Trophy', 'Crown', 'Target', 'Flag', 'Bookmark', 'Tag', 'Hash',
        ]
    },
    {
        key: 'files',
        label: 'Files & Folders',
        icons: [
            'File', 'FileText', 'FilePlus', 'FileCheck', 'FileX', 'Files',
            'Folder', 'FolderOpen', 'FolderPlus', 'FolderCheck', 'FolderX',
            'Archive', 'Package', 'Box', 'Inbox', 'Download', 'Upload',
            'Paperclip', 'Link', 'Image', 'Video', 'Music', 'Camera',
        ]
    },
    {
        key: 'communication',
        label: 'Communication',
        icons: [
            'Mail', 'MailOpen', 'Send', 'MessageSquare', 'MessageCircle',
            'Phone', 'PhoneCall', 'AtSign', 'Share', 'Share2',
            'Megaphone', 'Radio', 'Podcast', 'Rss', 'Wifi',
        ]
    },
    {
        key: 'productivity',
        label: 'Productivity',
        icons: [
            'Calendar', 'CalendarDays', 'Clock', 'Timer', 'Alarm',
            'CheckSquare', 'ListChecks', 'ListTodo', 'ClipboardList', 'ClipboardCheck',
            'Kanban', 'LayoutDashboard', 'BarChart', 'BarChart2', 'PieChart',
            'TrendingUp', 'Activity', 'Gauge', 'Brain', 'Lightbulb',
            'Pencil', 'PenTool', 'Highlighter', 'Type', 'Notebook',
        ]
    },
    {
        key: 'development',
        label: 'Development',
        icons: [
            'Code', 'Code2', 'Terminal', 'Braces', 'Binary',
            'Database', 'Server', 'HardDrive', 'Cpu', 'Monitor',
            'Laptop', 'Smartphone', 'Tablet', 'Keyboard', 'Mouse',
            'GitBranch', 'GitCommit', 'GitMerge', 'GitPullRequest', 'Github',
            'Bug', 'TestTube', 'Flask', 'Blocks', 'Puzzle',
        ]
    },
    {
        key: 'design',
        label: 'Design',
        icons: [
            'Palette', 'Paintbrush', 'Pipette', 'Figma', 'Layers',
            'Layout', 'LayoutGrid', 'Grid3x3', 'Columns', 'Rows',
            'Component', 'Frame', 'Move', 'Crop', 'Scissors',
            'Circle', 'Square', 'Triangle', 'Hexagon', 'Pentagon',
        ]
    },
    {
        key: 'business',
        label: 'Business',
        icons: [
            'Briefcase', 'Building', 'Building2', 'Store', 'ShoppingCart',
            'ShoppingBag', 'CreditCard', 'Wallet', 'Banknote', 'DollarSign',
            'Receipt', 'BadgeCheck', 'BadgeDollarSign', 'Handshake', 'Scale',
            'Gavel', 'Stamp', 'FileSpreadsheet', 'Presentation', 'Projector',
        ]
    },
    {
        key: 'media',
        label: 'Media',
        icons: [
            'Play', 'Pause', 'StopCircle', 'SkipForward', 'SkipBack',
            'Volume2', 'VolumeX', 'Mic', 'MicOff', 'Headphones',
            'Film', 'Clapperboard', 'MonitorPlay', 'Cast', 'Airplay',
            'ImagePlus', 'ScanLine', 'QrCode', 'Aperture', 'Focus',
        ]
    },
    {
        key: 'health',
        label: 'Health & Fitness',
        icons: [
            'Heart', 'HeartPulse', 'Stethoscope', 'Pill', 'Syringe',
            'Dumbbell', 'Bike', 'Footprints', 'PersonStanding', 'Bed',
            'Coffee', 'Apple', 'Salad', 'CookingPot', 'UtensilsCrossed',
        ]
    },
    {
        key: 'nature',
        label: 'Nature & Travel',
        icons: [
            'TreePine', 'Trees', 'Flower', 'Leaf', 'Sprout',
            'Mountain', 'Waves', 'Snowflake', 'Sunrise', 'Sunset',
            'Plane', 'Car', 'Train', 'Ship', 'Rocket',
            'MapPin', 'Map', 'Navigation', 'Compass', 'Tent',
        ]
    },
    {
        key: 'education',
        label: 'Education',
        icons: [
            'GraduationCap', 'BookOpen', 'Book', 'Library', 'School',
            'Backpack', 'Calculator', 'Ruler', 'Pen', 'Eraser',
            'Languages', 'Globe2', 'Microscope', 'Telescope', 'Atom',
        ]
    },
    {
        key: 'arrows',
        label: 'Arrows & Direction',
        icons: [
            'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
            'ArrowUpRight', 'ArrowDownLeft', 'MoveRight', 'MoveLeft',
            'CornerDownRight', 'CornerUpLeft', 'Undo', 'Redo',
            'RefreshCw', 'RotateCw', 'Repeat', 'Shuffle',
        ]
    },
];

// Icon tint color palette
export const ICON_COLORS = [
    { label: 'Default', value: null },
    { label: 'Gray',    value: '#9CA3AF' },
    { label: 'Brown',   value: '#C8956C' },
    { label: 'Orange',  value: '#F97316' },
    { label: 'Yellow',  value: '#EAB308' },
    { label: 'Green',   value: '#22C55E' },
    { label: 'Blue',    value: '#3B82F6' },
    { label: 'Purple',  value: '#8B5CF6' },
    { label: 'Pink',    value: '#EC4899' },
    { label: 'Red',     value: '#EF4444' },
];

/**
 * Search icons by name (case-insensitive, partial match).
 * Returns flat array of matching icon names.
 */
export function searchIcons(query) {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return ALL_ICON_NAMES.filter(name => {
        // Match camelCase words individually: "LayoutGrid" → "layout", "grid"
        const words = name.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
        return words.includes(q) || name.toLowerCase().includes(q);
    });
}

/**
 * Get all icons that exist in the current lucide-react installation.
 * Filters out any curated names that don't exist.
 */
export function getValidCategoryIcons(category) {
    return category.icons.filter(name => name in icons);
}
